import React from "react";
import FileUpload, {
	FileUploadType,
	splitFileUploads,
} from "elements/Input/FileUpload";
import { useState } from "react";
import client from "utils/client";
import { sendErrorToWebhook } from "utils/sendWebhook";
import { usePropertyContext } from "../../context";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import { useAuthContext } from "context/AuthContext";
import ButtonV2 from "elements/ButtonV2";
import { Checkbox, Label } from "elements/Input";
import moment from "moment";

const ResidencyProof = () => {
	const [error, setError] = useState(false);

	const {
		next,
		residencyProofFilesToUpload,
		setResidencyProofFilesToUpload,
		specialFieldsValue,
		setSpecialFieldsValue,
		moveInDateValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();

	const retroYears = React.useMemo(() => {
		const minQualifyingYear = moment().year() - 3;
		const qualifyingMoveInYear =
			moment(moveInDateValue).month() === 0 &&
			moment(moveInDateValue).date() === 1
				? moment(moveInDateValue).year()
				: moment(moveInDateValue).year() + 1;

		const firstQualifyingYear = Math.max(
			minQualifyingYear,
			qualifyingMoveInYear
		);
		const lastQualifyingYear = moment().year();

		let years = [];
		for (let year = firstQualifyingYear; year < lastQualifyingYear; year++) {
			years.push(year);
		}

		return years;
	}, [moveInDateValue]);

	const checkboxOptions = React.useMemo(() => {
		return [
			{
				label: "Voter Registration Card",
				name: "voters_registration_card",
				state: specialFieldsValue?.residency_proof_document_types?.includes(
					"voters_registration_card"
				),
			},
			{
				label: "Utility Bill",
				name: "utility_bill",
				state:
					specialFieldsValue?.residency_proof_document_types?.includes(
						"utility_bill"
					),
			},
			{
				label: "Income Tax Return",
				name: "income_tax_return",
				state:
					specialFieldsValue?.residency_proof_document_types?.includes(
						"income_tax_return"
					),
			},
			{
				label: "Medical Bills",
				name: "medical_bills",
				state:
					specialFieldsValue?.residency_proof_document_types?.includes(
						"medical_bills"
					),
			},
			{
				label: "Current Court Documents",
				name: "current_court_documents",
				state: specialFieldsValue?.residency_proof_document_types?.includes(
					"current_court_documents"
				),
			},
			{
				label: "Motor Vehicle Registration",
				name: "motor_vehicle_registration",
				state: specialFieldsValue?.residency_proof_document_types?.includes(
					"motor_vehicle_registration"
				),
			},
		];
	}, [specialFieldsValue]);

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;
		setSpecialFieldsValue({
			...specialFieldsValue,
			residency_proof_document_types: checked
				? [...(specialFieldsValue?.residency_proof_document_types ?? []), name]
				: (specialFieldsValue?.residency_proof_document_types ?? []).filter(
						type => type !== name
				  ),
		});
	};

	const handleSubmit = async () => {
		if (!details) return;
		setError(false);

		try {
			if (residencyProofFilesToUpload.length) {
				const { errors, success } = await splitFileUploads<{
					file_ref: string;
				}>({
					filesToUpload: residencyProofFilesToUpload,
					updateFilesToUpload: setResidencyProofFilesToUpload,
					buildRequest: async fileToUpload => {
						const fd = new FormData();
						fd.append("residencyProof", fileToUpload.file);
						const {
							data: { file_refs },
						} = await client.uploadExemptionFiles({
							formData: fd,
							propertyId: details!.id,
							onProgress: pe => {
								setResidencyProofFilesToUpload(prev =>
									prev.map(fu => {
										if (fu.file !== fileToUpload.file) return fu;
										return {
											...fu,
											progress: pe,
										};
									})
								);
							},
						});
						return { file_ref: file_refs["residencyProof"] };
					},
				});

				setSpecialFieldsValue({
					residency_proof_document_refs: [
						...(specialFieldsValue?.residency_proof_document_refs ?? []),
						...success.map(fileToUpload => fileToUpload.result.file_ref),
					],
				});

				if (errors.length > 0) {
					setError(true);
					return;
				}
			}
		} catch (e) {
			console.error("Exemptions - Error uploading residency proof: ", e);
			sendErrorToWebhook(
				"** Error Uploading Residency Proof Documents ** " + e
			);
			setError(true);
		}
	};

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					{details?.city === "Detroit" && (
						<p className="bold">
							The City of Detroit requires at least 1 proof of residency
							documentation for each year of occupancy on the application,
							separate from your driver's license.
						</p>
					)}
					{details?.city === "Ann Arbor" && (
						<p className="bold">
							The City of Ann Arbor requires 1 additional proof of residency for
							each year of occupancy on the application, separate from your
							driver's license.
						</p>
					)}
					{details?.city === "Sterling Heights" && (
						<p className="bold">
							The City of Sterling Heights requires at least 3 different proofs
							of residency documentation for the years on the application,
							separate from your driver's license.
						</p>
					)}
					{details?.city === "Grand Rapids" && (
						<p className="bold">
							The City of Grand Rapids requires at least 3 different proof of
							residency documentation for each year on the application, separate
							from your driver's license.
						</p>
					)}
				</div>
				<p className="exemption-question-card-subtitle sm mb-2">
					Please choose a document type and then upload all files for each
					applicable year: ({retroYears.join(", ")}). You may use a different
					document type for each year.
				</p>

				<Label>Select Document Types:*</Label>
				<div className="exemptions-residency-proof-checkbox-section mt-2">
					{checkboxOptions.map(({ label, name, state }, index) => (
						<div key={"document-types-" + name + index}>
							<Checkbox
								name={name}
								label={label}
								labelEl="p"
								checked={state ?? false}
								onChange={handleCheckboxChange}
							/>
						</div>
					))}
				</div>
				<FileUpload
					label="Upload All Documents"
					numOfFilesAccepted={10}
					filesToUpload={residencyProofFilesToUpload}
					updateFilesToUpload={setResidencyProofFilesToUpload}
				/>
				{residencyProofFilesToUpload.some(fu =>
					["pending", "staged"].includes(fu.status)
				) && (
					<ButtonV2
						size="mobile-extra-small"
						disabled={
							specialFieldsValue?.residency_proof_document_types?.length ===
								0 ||
							residencyProofFilesToUpload.length === 0 ||
							residencyProofFilesToUpload.some(fu =>
								["pending"].includes(fu.status)
							)
						}
						variant="primary"
						className="exemptions-continue-button">
						{residencyProofFilesToUpload.some(fu =>
							["pending"].includes(fu.status)
						)
							? "Uploading..."
							: "Upload"}
					</ButtonV2>
				)}
			</div>
			{error && (
				<p className="sm text-center terracotta mt-4">
					Whoops, something went wrong. Refresh your page and try again.
				</p>
			)}
			<ButtonV2
				size="large"
				type="button"
				onClick={() => {
					next();
				}}
				disabled={
					specialFieldsValue?.residency_proof_document_types?.length === 0 ||
					residencyProofFilesToUpload.length === 0 ||
					residencyProofFilesToUpload.some(fu =>
						["pending"].includes(fu.status)
					)
				}
				variant="primary"
				className="exemptions-continue-button">
				Continue
			</ButtonV2>
		</form>
	);
};

export default ResidencyProof;
